import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FireflyChipModule } from '../chips/chip.module';
import { ChipType } from '../chips/models/chip-type';
import { FireflyDropdownUtilsModule } from '../dropdowns/directives/dropdown.utils.module';
import { FireflyListModule } from '../lists/list.module';
import { FireflyModalService, ModalRef } from '../modal';
import { Breakpoint } from '../utils';
import { FireflyLocationChipGroupComponent } from './components/location-chip-group/location-chip-group.component';
import {
  FireflyLocationHierarchyItemComponent,
  HierarchySelectionEvent
} from './components/location-hierarchy-item/location-hierarchy-item.component';
import { LocationSuggesterMobileComponent } from './components/location-suggester-mobile/location-suggester-mobile.component';
import { LocationDataHelper } from './helpers/location-data.helper';
import {
  GroupedLocationItem,
  isGroupedItem,
  LocationItem,
  LocationItemData,
  RawLocationNode,
  SelectedPill
} from './models/location-item.model';
import { LocationSuggesterLabels, LocationSuggesterOptions } from './models/location-suggester-options.model';
import { LocationType } from './models/location-type.enum';
import { SelectionType } from './models/selection-type.enum';

const DEFAULT_LABELS: LocationSuggesterLabels = {
  placeholder: 'Search for location',
  excludeText: 'Exclude',
  excludedText: 'Excluded',
  includeText: 'Include',
  includedText: 'Included',
  tooltipToggle: 'Click to toggle Include or Exclude',
  noResults: 'No results found. Please, try another location'
};

const DEFAULT_OPTIONS = {
  groupingCount: 3,
  focusDelay: 250,
  suggest: { limit: 100, wordSeparator: ', ' }
};

/**
 * `f-location-suggester`
 *
 * Hierarchical location picker that supports Include/Exclude selection.
 * Implements ControlValueAccessor so it plugs directly into reactive and
 * template-driven Angular forms.
 *
 * ## Usage
 * ```html
 * <f-location-suggester
 *   [locationData]="rawLocationTree"
 *   [options]="locationOptions"
 *   [(ngModel)]="criteria.location">
 * </f-location-suggester>
 * ```
 *
 * Migrated from the AngularJS `search-advanced-location-suggester` directive
 * (locationSuggester.js, bd-monolith repo).
 */
@Component({
  selector: 'f-location-suggester',
  templateUrl: './location-suggester.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    FireflyChipModule,
    FireflyListModule,
    FireflyDropdownUtilsModule,
    FireflyLocationHierarchyItemComponent,
    FireflyLocationChipGroupComponent
  ],
  providers: [
    LocationDataHelper,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FireflyLocationSuggesterComponent,
      multi: true
    }
  ]
})
export class FireflyLocationSuggesterComponent implements ControlValueAccessor, OnInit, OnDestroy {
  /** Raw location tree from the host application (regions → countries → states → cities) */
  readonly locationData = input<RawLocationNode[]>([]);

  /** Label and behaviour overrides */
  readonly options = input<LocationSuggesterOptions>({});

  /** Placeholder text for the search input. Overrides options.labels.placeholder when set. */
  @Input() placeholder: string | undefined = undefined;

  /** External FormControl for the search input value. When provided its value drives the search text. */
  @Input() inputValue = new FormControl<string>({ value: '', disabled: false }, { nonNullable: true });

  /** Emits the current selection whenever it changes (include/exclude/remove). */
  @Output() selectionChange = new EventEmitter<LocationItemData[]>();

  @Output() dropdownOpened = new EventEmitter<void>();

  @ViewChild('inputEl') inputEl!: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') dropdown!: NgbDropdown;

  /** Optional template projected via `<ng-template #fNoDataTemplate>` — shown when locationData is empty. */
  @ContentChild('fNoDataTemplate') noDataTemplate: TemplateRef<unknown> | null = null;

  // ─── Angular forms hooks ─────────────────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: LocationItemData[]) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  // ─── Internal signal-based state ─────────────────────────────────────────
  private readonly _hierarchyItems = signal<LocationItem[]>([]);
  private readonly _flatItems = signal<LocationItem[]>([]);
  private readonly _selectedItems = signal<LocationItem[]>([]);
  private readonly _pendingModelValue = signal<LocationItemData[] | null>(null);

  readonly searchText = signal<string>('');
  protected readonly isFocused = signal<boolean>(false);
  readonly highlightedSuggestIndex = signal<number>(-1);
  protected readonly activeChipIndex = signal<number>(-1);

  // ─── Computed views ───────────────────────────────────────────────────────

  /** Expose the tree for the template */
  readonly hierarchyItems = computed(() => this._hierarchyItems());

  /** Expose the flat selection for the grouped-popover template and for external consumers (e.g. mobile modal). */
  readonly selectedItems = computed(() => this._selectedItems());

  /**
   * When >= groupingCount items are selected this collapses them into a single
   * synthetic "grouped" pill, matching the original behaviour from itemsManager.
   */
  protected readonly groupedItem = computed<GroupedLocationItem | null>(() => {
    const selected = this._selectedItems();
    const threshold = this.resolvedOptions.groupingCount;
    if (selected.length >= threshold) {
      return {
        key: 'ls_grouped_item',
        isGrouped: true,
        children: selected,
        data: { selectionType: SelectionType.Include, name: '' }
      };
    }
    return null;
  });

  /** Pills shown in the input area — either individual items or a single group pill */
  protected readonly displayedChips = computed<SelectedPill[]>(() => {
    const grouped = this.groupedItem();
    return grouped ? [grouped] : this._selectedItems();
  });

  /**
   * Typeahead results: filters the flat list by the search text, sorts by
   * relevance (totalCompanyEaum desc, companyCount desc) and limits to 100.
   */
  readonly suggestedItems = computed<LocationItem[]>(() => {
    const text = this.searchText().trim().toLowerCase();
    if (!text) return [];

    const { limit } = this.resolvedOptions.suggest;

    const matching = this._flatItems()
      .filter(item => {
        const idx = item.data.name.toLowerCase().indexOf(text);
        // mutate searchContext in-place (same approach as the original D3 widget)
        item.searchContext.startIdx = idx;
        item.searchContext.endIdx = idx >= 0 ? idx + text.length : -1;
        return idx >= 0;
      })
      .sort((a, b) => {
        const aEaum = a.data.relevance?.totalCompanyEaum ?? 0;
        const bEaum = b.data.relevance?.totalCompanyEaum ?? 0;
        if (bEaum !== aEaum) return bEaum - aEaum;
        const aCount = a.data.relevance?.companyCount ?? 0;
        const bCount = b.data.relevance?.companyCount ?? 0;
        return bCount - aCount;
      });

    return matching.slice(0, limit);
  });

  readonly showSuggest = computed(() => this.searchText().trim().length > 0);
  protected readonly showPlaceholder = computed(() => !this.isFocused() && this._selectedItems().length === 0);

  /** Chip items shaped for f-editable-chip-group (Record<string,unknown>[]) */
  readonly chipItems = computed(() =>
    this._selectedItems().map(
      item =>
        ({
          key: item.key,
          name: item.data.fullName || item.data.name,
          selectionLabel:
            item.data.selectionType === SelectionType.Include
              ? this.resolvedLabels.includedText
              : this.resolvedLabels.excludedText,
          selectionType: item.data.selectionType
        } as Record<string, unknown>)
    )
  );

  /** Excluded items passed as invalidItems → renders with danger style in collapsed popover */
  readonly excludedChipItems = computed(() =>
    this.chipItems().filter(c => c['selectionType'] === SelectionType.Exclude)
  );

  // ─── Expose enums/helpers to the template ────────────────────────────────
  readonly SelectionType = SelectionType;
  protected readonly isGroupedItem = isGroupedItem;
  readonly chipType = ChipType;

  modal: ModalRef<unknown> | null = null;
  private _mobileModalSnapshot: { item: LocationItem; originalType: SelectionType }[] | null = null;

  private blurTimerId: ReturnType<typeof setTimeout> | null = null;
  private inputValueSub: ReturnType<typeof this.inputValue.valueChanges.subscribe> | null = null;
  private lastWriteKey: string | null = null;

  constructor(
    private readonly dataHelper: LocationDataHelper,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: FireflyModalService
  ) {
    // React to locationData signal input changes
    effect(
      () => {
        const data = this.locationData();
        if (data?.length) {
          this._processLocationData(data);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    // Sync external inputValue FormControl → internal searchText signal
    this.inputValueSub = this.inputValue.valueChanges.subscribe(value => {
      this.searchText.set(value ?? '');
      this.highlightedSuggestIndex.set(-1);
      this.cdr.markForCheck();
    });
    // Reflect initial value
    if (this.inputValue.value) {
      this.searchText.set(this.inputValue.value);
    }
  }

  ngOnDestroy(): void {
    if (this.blurTimerId !== null) {
      clearTimeout(this.blurTimerId);
    }
    this.inputValueSub?.unsubscribe();
  }

  // ─── ControlValueAccessor ─────────────────────────────────────────────────

  writeValue(value: LocationItemData[] | null): void {
    if (!value || value.length === 0) {
      const emptyKey = this.buildWriteKey([], this._flatItems().length > 0);
      if (this.lastWriteKey === emptyKey) {
        return;
      }

      this._pendingModelValue.set(null);
      this._clearSelections();
      this.lastWriteKey = emptyKey;
      return;
    }

    const normalizedIncoming = value.map(item => ({
      ...item,
      selectionType: this._normalizeSelectionType(item.selectionType),
      typeName: item.typeName ?? item.type
    }));

    const writeKey = this.buildWriteKey(normalizedIncoming, this._flatItems().length > 0);
    if (this.lastWriteKey === writeKey) {
      return;
    }

    if (this._flatItems().length > 0) {
      this._applyModelValue(normalizedIncoming);
      this._pendingModelValue.set(null);
      this.lastWriteKey = writeKey;
    } else {
      // Data not loaded yet — store for deferred application
      // Important: still show chips immediately from form value even before
      // location hierarchy is loaded (lazy-load mode). We'll reconcile these
      // detached items against real hierarchy items once data arrives.
      const detached = this._createDetachedSelectedItems(normalizedIncoming);
      this._selectedItems.set(detached);
      this.cdr.markForCheck();

      this._pendingModelValue.set(normalizedIncoming);
      this.lastWriteKey = writeKey;
    }
  }

  registerOnChange(fn: (value: LocationItemData[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputValue.disable({ emitEvent: false });
    } else {
      this.inputValue.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  // ─── User interactions ────────────────────────────────────────────────────

  focusInput(): void {
    this.inputEl?.nativeElement.focus();
  }

  onInputFocus(): void {
    if (this.blurTimerId !== null) {
      clearTimeout(this.blurTimerId);
      this.blurTimerId = null;
    }
    if (window.innerWidth >= Breakpoint.Sm && !this.dropdown?.isOpen()) {
      this.dropdown?.open();
    }
  }

  openMobileModal(): void {
    if (window.innerWidth >= Breakpoint.Sm) {
      if (!this.dropdown?.isOpen()) {
        this.dropdown?.open();
      }
      return;
    }
    if (this.modal) return;

    this._mobileModalSnapshot = this._selectedItems().map(item => ({
      item,
      originalType: item.data.selectionType
    }));

    this.modal = this.modalService.open({
      component: LocationSuggesterMobileComponent,
      title: this.resolvedLabels.placeholder,
      context: this,
      mobile: true,
      modalDialogClass: 'mobile-suggester-modal'
    });

    this.modal.result
      .then(
        () => {
          // User accepted — state already applied, clear snapshot
          this._mobileModalSnapshot = null;
          this._notifyChange();
        },
        () => {
          // User dismissed — revert to snapshot
          this._resetMobileModalState();
        }
      )
      .finally(() => {
        this.modal = null;
        this.searchText.set('');
        this.cdr.markForCheck();
      });
  }

  private _resetMobileModalState(): void {
    if (this._mobileModalSnapshot === null) return;

    const current = this._selectedItems();

    // Reset items that were added during the modal (not in the snapshot)
    current
      .filter(item => !this._mobileModalSnapshot!.find(s => s.item === item))
      .forEach(item => {
        item.data.selectionType = SelectionType.None;
        item.isActive = false;
      });

    // Restore original selectionType for items that existed before the modal
    this._mobileModalSnapshot.forEach(({ item, originalType }) => {
      item.data.selectionType = originalType;
    });

    this._selectedItems.set(this._mobileModalSnapshot.map(s => s.item));
    this._mobileModalSnapshot = null;
    this._notifyChange();
    this.cdr.markForCheck();
  }

  onInputBlur(): void {
    this.blurTimerId = setTimeout(() => {
      this.onTouched();
      this.cdr.markForCheck();
    }, this.resolvedOptions.focusDelay);
  }

  onOpenChange(isOpen: boolean): void {
    this.isFocused.set(isOpen);
    if (isOpen) {
      this.dropdownOpened.emit();
    } else {
      this.inputValue.setValue('', { emitEvent: false });
      this.searchText.set('');
      this.highlightedSuggestIndex.set(-1);
      this.onTouched();
    }
  }

  /** Called when the user removes a chip from f-editable-chip-group */
  onChipRemove(chipItem: Record<string, unknown>): void {
    const key = chipItem['key'] as string;
    const item = this._selectedItems().find(i => i.key === key);
    if (item) this.removeItem(item);
  }

  /** Called when the user clicks a chip text (toggles Include ↔ Exclude) */
  onChipItemClick(chipItem: Record<string, unknown>): void {
    const key = chipItem['key'] as string;
    const item = this._selectedItems().find(i => i.key === key);
    if (item) this.toggleSelectionType(item);
  }

  /** Remove-all from collapsed chip group */
  onRemoveAll(): void {
    this._clearAll();
  }

  /** Clear the search input */
  clearSearch(event?: Event): void {
    event?.stopPropagation();
    this.inputValue.setValue('', { emitEvent: false });
    this.searchText.set('');
    this.highlightedSuggestIndex.set(-1);
    this.focusInput();
  }

  /** Called from the mobile template's native (input) event to update searchText */
  onTextInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchText.set(value);
    this.highlightedSuggestIndex.set(-1);
  }

  /** Add / select a location with an explicit selection type */
  selectItem(item: LocationItem, type: SelectionType, event?: Event): void {
    event?.stopPropagation();
    const current = this._selectedItems();
    item.data.selectionType = type;
    this._selectedItems.set(current.includes(item) ? [...current] : [...current, item]);
    this._notifyChange();
    this.cdr.markForCheck();
  }

  /** Toggle between Include ↔ Exclude (also used on city click in hierarchy) */
  toggleSelectionType(item: LocationItem): void {
    const newType = item.data.selectionType === SelectionType.Include ? SelectionType.Exclude : SelectionType.Include;
    this.selectItem(item, newType);
  }

  /** Remove a single chip (or clear all when the grouped chip is removed) */
  removeItem(item: SelectedPill, event?: Event): void {
    event?.stopPropagation();
    if (isGroupedItem(item)) {
      this._clearAll();
      return;
    }
    (item as LocationItem).data.selectionType = SelectionType.None;
    (item as LocationItem).isActive = false;
    this._selectedItems.update(items => items.filter(i => i !== item));
    this._notifyChange();
    this.cdr.markForCheck();
  }

  /** Keyboard navigation inside the input */
  onKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const hasText = target.value.length > 0;
    const selected = this._selectedItems();
    const hasSelected = selected.length > 0;

    // ── Navigation in selected chips (no text typed) ────────────────────
    if (hasSelected && !hasText) {
      const activeIdx = this.activeChipIndex();
      switch (event.key) {
        case 'Backspace': {
          if (activeIdx >= 0) {
            this.removeItem(selected[activeIdx]);
            this.activeChipIndex.set(-1);
          } else {
            const last = selected[selected.length - 1];
            last.isActive = true;
            this.activeChipIndex.set(selected.length - 1);
            this.cdr.markForCheck();
          }
          break;
        }
        case 'ArrowLeft': {
          event.preventDefault();
          if (activeIdx >= 0) selected[activeIdx].isActive = false;
          const prevIdx = Math.max(activeIdx <= 0 ? 0 : activeIdx - 1, 0);
          selected[prevIdx].isActive = true;
          this.activeChipIndex.set(prevIdx);
          this.cdr.markForCheck();
          break;
        }
        case 'ArrowRight': {
          event.preventDefault();
          if (activeIdx >= 0 && activeIdx < selected.length - 1) {
            selected[activeIdx].isActive = false;
            selected[activeIdx + 1].isActive = true;
            this.activeChipIndex.set(activeIdx + 1);
            this.cdr.markForCheck();
          }
          break;
        }
      }
    }

    // ── Navigation in typeahead list (while typing) ─────────────────────
    if (hasText) {
      const suggestions = this.suggestedItems();
      const hlIdx = this.highlightedSuggestIndex();

      switch (event.key) {
        case 'ArrowUp': {
          event.preventDefault();
          const prev = hlIdx > 0 ? hlIdx - 1 : hlIdx;
          this.highlightedSuggestIndex.set(prev);
          break;
        }
        case 'ArrowDown': {
          event.preventDefault();
          const next = hlIdx < suggestions.length - 1 ? hlIdx + 1 : Math.max(0, hlIdx);
          this.highlightedSuggestIndex.set(next < 0 ? 0 : next);
          break;
        }
        case 'Enter': {
          event.preventDefault();
          const highlighted = suggestions[hlIdx];
          if (highlighted) {
            this.toggleSelectionType(highlighted);
            target.select();
          }
          break;
        }
      }
    }
  }

  // ─── Hierarchy panel events ───────────────────────────────────────────────

  onHierarchySelect(event: HierarchySelectionEvent): void {
    this.selectItem(event.item, event.type);
    this.focusInput();
  }

  /** Row-click in the hierarchy — expand/collapse only; include/exclude is handled by the action links */
  onHierarchyToggle(_item: LocationItem): void {
    this.cdr.markForCheck();
    this.focusInput();
  }

  // ─── Template helpers ─────────────────────────────────────────────────────

  /** Text label for the grouped chip, e.g. "2 Included, 1 Excluded" */
  buildGroupText(item: GroupedLocationItem): string {
    const { includedText, excludedText } = this.resolvedLabels;
    let inc = 0;
    let exc = 0;
    for (const child of item.children) {
      if (child.data.selectionType === SelectionType.Include) inc++;
      if (child.data.selectionType === SelectionType.Exclude) exc++;
    }
    const parts: string[] = [];
    if (inc > 0) parts.push(`${inc} ${includedText}`);
    if (exc > 0) parts.push(`${exc} ${excludedText}`);
    return parts.join(', ');
  }

  /**
   * Builds the text-after suffix shown in the typeahead:
   * "...rest of name, State, Country (City)"
   */
  getSuggestSuffix(item: LocationItem): string {
    const { wordSeparator } = this.resolvedOptions.suggest;
    const parts: string[] = [];
    parts.push(item.data.name.substring(item.searchContext.endIdx));

    let parent = item.parent;
    while (parent) {
      if (parent.data.type !== LocationType.Region) {
        parts.push(parent.data.name);
      }
      parent = parent.parent;
    }

    return parts.join(wordSeparator) + ` (${item.data.typeName})`;
  }

  /** Reactive map of key → SelectionType derived from the selectedItems signal.
   * Reading this in template method calls ensures the suggest list reflects
   * both freshly-selected and pre-loaded (writeValue) items correctly. */
  private readonly _selectionMap = computed(
    () => new Map(this._selectedItems().map(s => [s.key, s.data.selectionType]))
  );

  isItemActive(item: LocationItem): boolean {
    const map = this._selectionMap();
    let current: LocationItem | null = item;
    while (current) {
      const type = map.get(current.key);
      if (type !== undefined && type !== SelectionType.None) return true;
      current = current.parent;
    }
    return false;
  }

  isItemIncluded(item: LocationItem): boolean {
    return this._effectiveType(item) === SelectionType.Include;
  }

  isItemExcluded(item: LocationItem): boolean {
    return this._effectiveType(item) === SelectionType.Exclude;
  }

  get resolvedLabels(): LocationSuggesterLabels {
    const labels = { ...DEFAULT_LABELS, ...this.options()?.labels };
    return this.placeholder !== undefined ? { ...labels, placeholder: this.placeholder! } : labels;
  }

  get resolvedOptions() {
    const opts = this.options();
    return {
      groupingCount: opts?.groupingCount ?? DEFAULT_OPTIONS.groupingCount,
      focusDelay: opts?.focusDelay ?? DEFAULT_OPTIONS.focusDelay,
      suggest: {
        limit: opts?.suggest?.limit ?? DEFAULT_OPTIONS.suggest.limit,
        wordSeparator: opts?.suggest?.wordSeparator ?? DEFAULT_OPTIONS.suggest.wordSeparator
      }
    };
  }

  trackByKey(_: number, item: SelectedPill): string {
    return item.key;
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private _processLocationData(raw: RawLocationNode[]): void {
    const hierarchy = this.dataHelper.createItems(raw);
    const flat = this.dataHelper.flatten(hierarchy);

    this._hierarchyItems.set(hierarchy);
    this._flatItems.set(flat);

    const pending = this._pendingModelValue();
    if (pending) {
      this._applyModelValue(pending);
      this._pendingModelValue.set(null);
    }
  }

  private _applyModelValue(values: LocationItemData[]): void {
    this._clearSelections();
    const flat = this._flatItems();
    const newSelected: LocationItem[] = [];

    for (const val of values) {
      // 1. Exact composite-key match ("type|key").
      const exactKey = this.dataHelper.fullDataId(val);
      let found = flat.find(item => item.key === exactKey);

      // 2. typeName + key fallback: handles type-token drift (e.g. 'country' vs
      // 'countryTerritory') while preventing cross-type collisions (region key 4
      // must not match country key 4).
      if (!found) {
        const valTypeName = (val.typeName ?? val.type).toLowerCase();
        found = flat.find(
          item =>
            String(item.data.key) === String(val.key) &&
            (item.data.typeName ?? item.data.type).toLowerCase() === valTypeName
        );
      }

      if (found) {
        found.data.selectionType = this._normalizeSelectionType(val.selectionType);
        newSelected.push(found);
      } else {
        // Node not present in the current tree (e.g. legacy ID, filtered subset).
        // Show as a detached chip using the saved display name so the selection
        // remains visible rather than being silently dropped.
        const [detached] = this._createDetachedSelectedItems([val]);
        newSelected.push(detached);
      }
    }

    this._selectedItems.set(newSelected);
    this.cdr.markForCheck();
  }

  private _createDetachedSelectedItems(values: LocationItemData[]): LocationItem[] {
    return values.map(item => {
      const normalized: LocationItemData = {
        ...item,
        selectionType: this._normalizeSelectionType(item.selectionType),
        typeName: item.typeName ?? item.type
      };

      return {
        key: this.dataHelper.fullDataId(normalized),
        parent: null,
        isCollapsed: true,
        isChildrenRendered: false,
        isActive: false,
        data: normalized,
        searchContext: { startIdx: -1, endIdx: -1 }
      };
    });
  }

  private buildWriteKey(values: LocationItemData[], flatReady: boolean): string {
    if (!values.length) {
      return `empty|flat:${flatReady ? 1 : 0}`;
    }

    const tokens = values
      .map(v => `${v.type}|${v.key}|${this._normalizeSelectionType(v.selectionType)}`)
      .sort()
      .join(',');

    return `${tokens}|flat:${flatReady ? 1 : 0}`;
  }

  private _normalizeSelectionType(selectionType: SelectionType | string | undefined): SelectionType {
    if (selectionType === SelectionType.Exclude || selectionType === 'exclude') {
      return SelectionType.Exclude;
    }
    if (selectionType === SelectionType.Include || selectionType === 'include') {
      return SelectionType.Include;
    }
    return SelectionType.None;
  }

  private _clearSelections(): void {
    this._selectedItems().forEach(item => {
      item.data.selectionType = SelectionType.None;
      item.isActive = false;
    });
    this._selectedItems.set([]);
  }

  private _clearAll(): void {
    this._clearSelections();
    this._notifyChange();
    this.cdr.markForCheck();
  }

  private _notifyChange(): void {
    const value = this._selectedItems().map(item => ({ ...item.data }));
    this.onChange(value);
    this.selectionChange.emit(value);
  }

  private _effectiveType(item: LocationItem): SelectionType {
    const map = this._selectionMap();
    let current: LocationItem | null = item;
    while (current) {
      const type = map.get(current.key);
      if (type !== undefined && type !== SelectionType.None) return type;
      current = current.parent;
    }
    return SelectionType.None;
  }
}
